import { FullStory } from '@fullstory/browser';
import { useDownloadContract, useGenerateTpvUrl } from 'api/agreement';
import { AxiosError } from 'axios';
import useOnboardingStep from 'hooks/useOnboardingStep';
import useUser from 'hooks/useUser';
import { useBreakpoints } from 'hooks/useWindowSize';
import { isWithinTPVBusinessHours } from 'modules/status/utils/tpvBusinessHours';
import React, { Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from 'shared/components/Button/Button';
import { DownloadIcon } from 'shared/components/icons';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import Text from 'shared/components/Text/Text';
import { toast } from 'shared/components/Toast/Toast';
import DataDogLogs from 'shared/logging/DataDogLogs';
import { useTheme } from 'styled-components';
import { OnboardingStep } from 'modules/types/util';
import {
  ContentItem,
  ContentList,
  TPVUnverifiedContainer,
  OnboardingTPVContent,
} from './OnboardingTPV.styles';
import { TPVAgentSchedule } from './TPVAgentSchedule';
import { TpvUnavailableModal } from '../../TpvUnavailableModal/TpvUnavailableModal';

const AgreementReviewIcon = React.lazy(() => import('shared/components/icons/AgreementReviewIcon'));

export function TPVUnverified() {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.tpv' });
  const { goToStep } = useOnboardingStep();
  const theme = useTheme();
  const { selectedAgreement, refetch: refetchUser } = useUser();

  const [isCallLoading, setIsCallLoading] = useState(false);
  const screenSize = useBreakpoints();
  const [isTpvUnavailableModalOpen, setIsTpvUnavailableModalOpen] = useState(false);

  const handleError = ({
    title,
    additionalInfo,
    error,
  }: {
    title: string;
    additionalInfo: Record<string, unknown>;
    error: Error | AxiosError<unknown, any>;
  }) => {
    toast({
      type: 'error',
      title: t('toast.error', { keyPrefix: '' }),
      message: t('somethingWentWrong'),
      theme,
    });

    DataDogLogs.error(title, { additionalInfo }, error);
  };

  const { mutateAsync: generateTpvUrlAsync } = useGenerateTpvUrl({
    onError(error: AxiosError) {
      handleError({
        title: 'Onboarding - TPV Url Generation',
        additionalInfo: { agreementId: selectedAgreement?.id },
        error,
      });
    },
  });

  const { mutate: downloadContract, isLoading: contractIsLoading } = useDownloadContract({
    onSuccess: (data) => {
      const { presigned_url: presignedUrl } = data;
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = 'signed_everbright_contract.pdf';
      link.click();
      link.remove();
    },
    onError: (error) => {
      handleError({
        title: 'Onboarding - Contract download',
        additionalInfo: { agreementId: selectedAgreement?.id },
        error,
      });
    },
  });

  const handleDownloadContract = () => {
    if (selectedAgreement?.id) {
      downloadContract(selectedAgreement.id);
    } else {
      handleError({
        title: 'Onboarding - Contract download',
        additionalInfo: { agreementId: selectedAgreement?.id },
        error: new Error('No selected agreement for this user'),
      });
    }

    FullStory('trackEvent', {
      name: 'Onboarding - TPV Download Contract',
      properties: {
        page: 'Status',
        onboardingStep: 'TPV',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const bodyList: string[] = t('body', {
    returnObjects: true,
  }) as string[];

  const handleStartCallClick = async () => {
    if (!isWithinTPVBusinessHours()) {
      setIsTpvUnavailableModalOpen(true);
      return;
    }
    const agreementId = selectedAgreement?.id;
    if (!agreementId) {
      handleError({
        title: 'Onboarding - Start TPV call',
        additionalInfo: { agreementId: selectedAgreement?.id },
        error: new Error('No selected agreement for this user'),
      });
      return;
    }
    try {
      setIsCallLoading(true);
      const tpvUrlResponse = await generateTpvUrlAsync({ agreement_id: agreementId! });
      FullStory('trackEvent', {
        name: 'Start TPV Call - Clicked',
        properties: {
          page: 'Status',
          onboardingMilestone: selectedAgreement?.onboarding_milestone,
          status: 'success',
        },
      });
      // This is a known issue in Safari as it will not open new tab inside an async function.
      // Using then() does not work for latest Safari version.
      // Using setTimeout() works fine. setTimeout code runs on the main thread, instead of the asynchronous one
      // See: https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
      setTimeout(() => {
        window.open(tpvUrlResponse.url, '_blank');
      });
    } catch (error: any) {
      handleError({
        title: 'Onboarding - Start TPV Call',
        additionalInfo: { agreementId: selectedAgreement?.id },
        error,
      });
      FullStory('trackEvent', {
        name: 'Start TPV Call - Clicked',
        properties: {
          page: 'Status',
          onboardingMilestone: selectedAgreement?.onboarding_milestone,
          status: 'error',
        },
      });
    } finally {
      refetchUser();
      setIsCallLoading(false);
    }
  };

  const handleSkipStep = () => {
    FullStory('trackEvent', {
      name: 'Onboarding - Clicked Skip For Now',
      properties: {
        page: 'Status',
        onboardingStep: 'TPV',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });

    goToStep(OnboardingStep.AUTOPAY);
  };

  return (
    <TPVUnverifiedContainer>
      <Suspense fallback={<LoadingSpinner />}>
        <AgreementReviewIcon />
      </Suspense>
      <Text as="h2" $textAlign="center" $fontWeight={500} $margin="16px" $fontSize={24}>
        {t('title')}
      </Text>
      <Button
        styleVariant="tertiary"
        label={t('download')}
        Icon={DownloadIcon}
        onClick={handleDownloadContract}
        isLoading={contractIsLoading}
        isFullWidth={screenSize === 'sm'}
      />

      <OnboardingTPVContent>
        <ContentList>
          {Array.isArray(bodyList) &&
            bodyList.map((item) => (
              <ContentItem key={item}>
                <Trans>{item}</Trans>
              </ContentItem>
            ))}
        </ContentList>
      </OnboardingTPVContent>
      <Button
        styleVariant="primary"
        label={t('startCall')}
        onClick={handleStartCallClick}
        isLoading={isCallLoading}
        margin="16px 0 12px"
        isFullWidth={screenSize === 'sm'}
      />
      <Button
        styleVariant="tertiary"
        label={t('skipForNow')}
        onClick={handleSkipStep}
        isFullWidth={screenSize === 'sm'}
      />
      <TPVAgentSchedule />
      <TpvUnavailableModal
        isOpen={isTpvUnavailableModalOpen}
        onCloseModal={() => setIsTpvUnavailableModalOpen(false)}
      />
    </TPVUnverifiedContainer>
  );
}

export default TPVUnverified;
